import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient'; 
import { Auth } from '@supabase/auth-ui-react'; 
import { ThemeSupa } from '@supabase/auth-ui-shared'; 

import TabNavigation from './components/TabNavigation';
import LogisticsForm from './components/LogisticsForm';
import RMAForm from './components/RMAForm';
import DeliveryTracker from './components/DeliveryTracker';
import QRGenerator from './components/QRGenerator';
import RecordsViewer from './components/RecordsViewer';

// Importar estilos
import './styles/main.css';

function App() {
  // Estado de autenticación
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appError, setAppError] = useState(null);
  const [serverStatus, setServerStatus] = useState({
    isConnected: false, 
    error: null,
    loading: true, 
  });

  // Estado de la aplicación
  const [activeTab, setActiveTab] = useState('Logística');
  
  // Estado para etiquetas de logística
  const [sku, setSku] = useState('');
  const [serialNumbers, setSerialNumbers] = useState('');
  
  // Estado para etiquetas RMA
  const [rma, setRma] = useState('');
  const [isPallet, setIsPallet] = useState('');
  const [packagingType, setPackagingType] = useState('');
  const [packagingCondition, setPackagingCondition] = useState('');
  const [bulkType, setBulkType] = useState('');
  const [customBulkNumber, setCustomBulkNumber] = useState('');
  const [images, setImages] = useState([]);
  
  // Estado compartido
  const [labelSizes, setLabelSizes] = useState({
    large: false,
    small: false,
  });
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [validationError, setValidationError] = useState('');
  const [qrData, setQrData] = useState('');

  // --- Lógica de autenticación de Supabase ---
  useEffect(() => {
    setLoading(true);
    
    try {
      supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
        setSession(currentSession);
        setLoading(false);
        if (currentSession) {
          setServerStatus({ isConnected: true, error: null, loading: false });
        } else {
          setServerStatus({ isConnected: false, error: null, loading: false });
        }
      }).catch(error => {
        console.error("Error getting session:", error);
        setAppError("Error de autenticación: " + error.message);
        setLoading(false);
        setServerStatus({ isConnected: false, error: 'Error al obtener sesión de Supabase.', loading: false });
      });
    } catch (error) {
      console.error("Error crítico en la aplicación:", error);
      setAppError("Error crítico en la aplicación: " + error.message);
      setLoading(false);
      setServerStatus({ isConnected: false, error: 'Error crítico de inicialización.', loading: false });
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, changedSession) => {
      setSession(changedSession);
      if (changedSession) {
        setServerStatus({ isConnected: true, error: null, loading: false });
      } else {
        setServerStatus({ isConnected: false, error: null, loading: false });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);
  // --- Fin de la lógica de autenticación ---

  const handleImageCapture = (updatedImages) => {
    setImages(updatedImages);
  };

  const getBulkNumber = () => {
    if (bulkType === 'one') return '1';
    if (bulkType === 'two') return '2';
    if (bulkType === 'custom') return customBulkNumber;
    return '';
  };

  const validateRMAForm = () => {
    if (!rma.trim()) {
      setValidationError('Por favor, introduce el número de RMA');
      return false;
    }
    if (!isPallet) {
      setValidationError('Por favor, selecciona si viene en pallet o no');
      return false;
    }
    if (!packagingType) {
      setValidationError('Por favor, selecciona el tipo de embalaje');
      return false;
    }
    if (!packagingCondition) {
      setValidationError('Por favor, selecciona el estado del embalaje');
      return false;
    }
    if (!bulkType) {
      setValidationError('Por favor, selecciona el número de bultos');
      return false;
    }
    if (bulkType === 'custom' && !customBulkNumber.trim()) {
      setValidationError('Por favor, especifica el número de bultos');
      return false;
    }
    if (!labelSizes.large && !labelSizes.small) {
      setValidationError('Por favor, selecciona al menos un tamaño de etiqueta');
      return false;
    }
    if (images.length === 0) {
      setValidationError('Por favor, añade al menos una imagen');
      return false;
    }
    setValidationError('');
    return true;
  };

  const generateQrCode = async () => {
    if (activeTab === 'RMA' && !validateRMAForm()) {
      return;
    }

    try {
      let qrContent = '';

      if (activeTab === 'Logística') {
        if (!sku.trim()) {
          setValidationError('Por favor, introduce el SKU');
          return;
        }
        if (!labelSizes.large && !labelSizes.small) {
          setValidationError('Por favor, selecciona al menos un tamaño de etiqueta');
          return;
        }

        qrContent = `SKU: ${sku}`;
        if (serialNumbers.trim()) {
          qrContent += `\nNúmeros de Serie: ${serialNumbers}`;
        }

      } else if (activeTab === 'RMA') {
        qrContent = `RMA: ${rma}\nPallet: ${isPallet}\nEmbalaje: ${packagingType}\nEstado: ${packagingCondition}\nBultos: ${getBulkNumber()}`;
      }

      setQrData(qrContent);
      
      // La generación actual del QR se maneja en el componente QRGenerator
    } catch (error) {
      console.error('Error generating QR code:', error);
      setValidationError('Error al generar el código QR');
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="spinner"></div>
        <p className="text-center">Cargando aplicación...</p>
      </div>
    );
  }

  if (appError) {
    return (
      <div className="app-container">
        <div className="alert error">
          <span role="img" aria-label="Error">⚠️</span> {appError}
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app-container">
        <div className="app-header">
          <h1>Generador de Etiquetas QR</h1>
        </div>
        <div className="app-content">
          <Auth
            supabaseClient={supabase}
            appearance={{ theme: ThemeSupa }}
            providers={[]}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="app-header">
        <h1>Generador de Etiquetas QR</h1>
      </div>

      <div className="app-content">
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* QR Generator que se ejecuta cuando cambia qrData */}
        <QRGenerator data={qrData} setQrCodeDataUrl={setQrCodeDataUrl} />

        {activeTab === 'Logística' && (
          <LogisticsForm
            sku={sku}
            setSku={setSku}
            serialNumbers={serialNumbers}
            setSerialNumbers={setSerialNumbers}
            labelSizes={labelSizes}
            setLabelSizes={setLabelSizes}
            qrCodeDataUrl={qrCodeDataUrl}
            generateQrCode={generateQrCode}
          />
        )}

        {activeTab === 'RMA' && (
          <RMAForm
            rma={rma}
            setRma={setRma}
            isPallet={isPallet}
            setIsPallet={setIsPallet}
            packagingType={packagingType}
            setPackagingType={setPackagingType}
            packagingCondition={packagingCondition}
            setPackagingCondition={setPackagingCondition}
            bulkType={bulkType}
            setBulkType={setBulkType}
            customBulkNumber={customBulkNumber}
            setCustomBulkNumber={setCustomBulkNumber}
            labelSizes={labelSizes}
            setLabelSizes={setLabelSizes}
            images={images}
            onImageCapture={handleImageCapture}
            validationError={validationError}
            qrCodeDataUrl={qrCodeDataUrl}
            generateQrCode={generateQrCode}
          />
        )}

        {activeTab === 'Entregas' && (
          <DeliveryTracker userId={session.user.id} />
        )}

        {activeTab === 'Registros' && (
          <RecordsViewer activeTab="logistics" />
        )}

        {activeTab === 'Registros RMA' && (
          <RecordsViewer activeTab="rma" />
        )}
      </div>
    </div>
  );
}

export default App;